import { Box, Button } from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ItemCount from "../../../utils/ItemCount";
import LogisticsDraftTable from "./LogisticsDraftTable";
import LogisticRfqTable from "./LogisticRfqTable";
import QueryTable from "./QueryTable";

const datagridSx = {
  position: "relative",
  border: "none",
  fontFamily: "Inter !important",
  "& .MuiDataGrid-columnHeaderTitle": { fontWeight: "700 !important" },
  "& .MuiDataGrid-columnHeaders": {
    background: "none !important",
    color: "black",
    fontSize: "14px",
    borderRadius: "10px",
  },
  "& .MuiDataGrid-row:hover": { backgroundColor: "#fff" },
  "& .MuiDataGrid-cell": { fontSize: "13px", border: "none" },
  "& .MuiDataGrid-columnSeparator": { display: "none" },
  "& .css-1ccsd5i-MuiButtonBase-root-MuiButton-root": {
    color: "#000 !important",
    border: "1px solid #B0B0B0",
    borderRadius: "12px",
    padding: 1,
  },
  "& .MuiInput-underline:before": { borderBottom: "none !important" },
  "& .css-5h82ro-MuiInputBase-root-MuiInput-root::after": {
    borderBottom: "none !important",
  },
};

const LogisticDashboard = () => {
  const storedUser = JSON.parse(localStorage.getItem("user")) || {};
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState(
    localStorage.getItem("logisticActiveTab") || "PROGRESS",
  );
  const [isLoading, setIsLoading] = useState(true);
  const [rfqs, setRfqs] = useState();

  // counts
  const [allCount, setAllCount] = useState(0);
  const [draftCount, setDraftCount] = useState(0);
  const [actionCount, setActionCount] = useState(0);
  const [recommendationCount, setRecommendationCount] = useState(0);
  const [concludedCount, setConcludedCount] = useState(0);
  const [queryCount, setQueryCount] = useState(0);

  const activeStatuses = [
    "ACCEPTING_BIDS",
    "BIDS_CLOSED",
    "REBIDDING",
    "EAUCTIONED",
    "SUBMITTED",
  ];

  // ── fetch counts ─────────────────────────
  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/logistic-draft?username=${storedUser.username}`,
          { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
        );

        const all = res.data || [];

        setAllCount(
          all.filter((r) => activeStatuses.includes(r.status)).length,
        );

        setConcludedCount(
          all.filter(
            (r) => r.status === "AWARDED" || r.quoteStatus === "AWARDED",
          ).length,
        );

        setDraftCount(
          all.filter(
            (r) => !activeStatuses.includes(r.status) && r.status !== "AWARDED",
          ).length,
        );
      } catch (error) {
        console.log(error);
      }
    };

    const fetchActionCount = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/logistic/quote/count-action?username=${storedUser.username}`,
          { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
        );
        setActionCount(res.data);
      } catch (error) {
        console.log(error);
      }
    };
    const fetchRecommendationCount = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/logistic/quotes/count-recommendation?username=${storedUser.username}`,
          { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
        );
        setRecommendationCount(res.data);
      } catch (error) {
        console.log(error);
      }
    };
    const fetchQueryCount = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/query/logistic-count?username=${storedUser.username}`,
          { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
        );
        setQueryCount(res.data);
      } catch (error) {
        console.log(error);
      }
    };

    fetchCounts();
    fetchActionCount();
    fetchQueryCount();
    fetchRecommendationCount();
  }, []);

  // ── fetch data based on tab ─────────────────────────
  useEffect(() => {
    const fetchTabData = async () => {
      setRfqs();
      setIsLoading(true);

      try {
        let res;

        if (activeTab === "PROGRESS") {
          res = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/logistic-draft?username=${storedUser.username}`,
            { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
          );

          setRfqs(
            (res.data || []).filter((r) => activeStatuses.includes(r.status)),
          );
        } else if (activeTab === "CONCLUDED") {
          res = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/logistic-draft?username=${storedUser.username}`,
            { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
          );

          setRfqs(
            (res.data || []).filter(
              (r) => r.status === "AWARDED" || r.quoteStatus === "AWARDED",
            ),
          );
        } else if (activeTab === "ACTION") {
          res = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/logistic/quote/action-required?username=${storedUser.username}`,
            { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
          );

          setRfqs(res.data || []);
        } else if (activeTab === "RECOMMENDATION") {
          res = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/logistic/quote/recommendation?username=${storedUser.username}`,
            { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
          );

          setRfqs(res.data || []);
        } else if (activeTab === "QUERY") {
          res = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/query/get-logistic-rfqs?username=${storedUser.username}`,
            { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
          );

          setRfqs(res.data || []);
        }
      } catch (error) {
        console.log(error);
      } finally {
        setIsLoading(false);
      }
    };

    if (activeTab !== "DRAFTS") {
      fetchTabData();
    } else {
      setIsLoading(false);
    }
  }, [activeTab]);

  const switchTab = (tab) => {
    setActiveTab(tab);
    setIsLoading(true);
    localStorage.setItem("logisticActiveTab", tab);
  };

  return (
    <Box sx={{ textAlign: "center" }}>
      {storedUser.userRole.includes("BUYER") && (
        <Box sx={{ display: "flex", justifyContent: "end", gap: 3 }}>
          <Button
            className="btn-contained"
            onClick={() => navigate("logistic-creation")}
          >
            + New Logistics RFQ
          </Button>
        </Box>
      )}

      <Box className="dashboard-container" sx={{ height: 400, width: "100%" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mb: 2,
            borderBottom: "1px solid #D1D1D1",
          }}
        >
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              className={`dashboard-btns ${activeTab === "PROGRESS" ? "active" : ""}`}
              onClick={() => switchTab("PROGRESS")}
            >
              In Quotes
              <ItemCount count={allCount} />
            </Button>

            {storedUser.userRole.includes("BUYER") && (
              <Button
                className={`dashboard-btns ${activeTab === "DRAFTS" ? "active" : ""}`}
                onClick={() => switchTab("DRAFTS")}
              >
                My Drafts
                <ItemCount count={draftCount} />
              </Button>
            )}

            <Button
              className={`dashboard-btns ${activeTab === "ACTION" ? "active" : ""}`}
              onClick={() => switchTab("ACTION")}
            >
              Action Required
              <ItemCount count={actionCount} />
            </Button>

            <Button
              className={`dashboard-btns ${activeTab === "CONCLUDED" ? "active" : ""}`}
              onClick={() => switchTab("CONCLUDED")}
            >
              Concluded
              <ItemCount count={concludedCount} />
            </Button>

            <Button
              className={`dashboard-btns ${activeTab === "RECOMMENDATION" ? "active" : ""}`}
              onClick={() => switchTab("RECOMMENDATION")}
            >
              In Recommendation
              <ItemCount count={recommendationCount} />
            </Button>

            {storedUser.userRole.includes("BUYER") && (
              <Button
                className={`dashboard-btns ${activeTab === "QUERY" ? "active" : ""}`}
                onClick={() => switchTab("QUERY")}
              >
                Query
                <ItemCount count={queryCount} />
              </Button>
            )}
          </Box>
        </Box>

        {activeTab === "DRAFTS" ? (
          <LogisticsDraftTable />
        ) : activeTab === "ACTION" ? (
          <LogisticRfqTable
            rfqs={rfqs}
            activeTab={activeTab}
            isLoading={isLoading}
            showActionBtn
          />
        ) : activeTab === "RECOMMENDATION" ? (
          <LogisticRfqTable
            rfqs={rfqs}
            activeTab={activeTab}
            isLoading={isLoading}
            showQuoteStatus
          />
        ) : activeTab === "QUERY" ? (
          <QueryTable
            rfqs={rfqs}
            activeTab={activeTab}
            isLoading={isLoading}
            storedUser={storedUser}
          />
        ) : (
          <LogisticRfqTable
            rfqs={rfqs}
            activeTab={activeTab}
            isLoading={isLoading}
          />
        )}
      </Box>
    </Box>
  );
};

export default LogisticDashboard;
